import { Box, Badge, Text, HStack, Progress, Icon } from '@chakra-ui/react';
import { FiAlertTriangle } from 'react-icons/fi'
import { useDraggable } from '@dnd-kit/core'
import type { ItineraryTurfPoolDto } from '../../../types/campaign'
import { getTurfColor } from '../../../lib/mapConfig'
import { doorsAndPeople } from '../../../lib/turfCounts'
import { FollowUpKindBadge } from '../followUp/FollowUpKindBadge'

interface ItineraryTurfPoolCardProps {
  turf: ItineraryTurfPoolDto
}

export function ItineraryTurfPoolCard({ turf }: ItineraryTurfPoolCardProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `pool:${turf.turfId}`,
    data: { type: 'pool-turf', turf },
  })

  const isFollowUp = turf.purpose === 'FollowUp'
  const contactRate = turf.memberCount > 0
    ? Math.round((turf.visitedCount / turf.memberCount) * 100)
    : 0

  const progressColor = contactRate >= 80 ? 'green' : contactRate >= 40 ? 'yellow' : 'red'
  const turfColor = getTurfColor(turf.turfIndex)

  const style = transform
    ? { transform: `translate(${transform.x}px, ${transform.y}px)`, opacity: isDragging ? 0.5 : 1 }
    : undefined

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      bg="surface.2"
      border="1px solid"
      borderColor={turf.needsFollowUp ? 'orange.300' : 'gray.200'}
      borderLeft="4px solid"
      borderLeftColor={turfColor}
      borderRadius="md"
      p={2}
      mb={1}
      cursor="grab"
      _hover={{ borderColor: 'accent', shadow: 'sm' }}
      fontSize="xs"
    >
      <HStack gap={1} align="start">
        {turf.needsFollowUp && (
          <Icon color="orange.500" mt="2px" flexShrink={0} asChild><FiAlertTriangle /></Icon>
        )}
        <Box flex={1}>
          <HStack gap={1}>
            <Box w="8px" h="8px" borderRadius="sm" bg={turfColor} flexShrink={0} mt="2px" />
            <Text fontWeight="semibold" lineHeight="short" lineClamp={1}>
              {turf.turfLabel}
            </Text>
          </HStack>
          <Text color="gray.500" lineHeight="short" lineClamp={1}>
            {turf.turfMapLabel}
          </Text>
          <HStack gap={1} mt={1} flexWrap="wrap">
            {isFollowUp && turf.followUpKind && (
              <FollowUpKindBadge kind={turf.followUpKind} customName={turf.followUpCustomKindName} />
            )}
            <Badge variant="outline" size="sm" fontSize="2xs">
              {isFollowUp ? `${turf.doorCount ?? turf.memberCount} stops` : doorsAndPeople(turf.doorCount, turf.memberCount)}
            </Badge>
            {turf.totalPassesScheduled > 0 && (
              <Badge variant="subtle" bg="info.subtle" color="info.fg" size="sm" fontSize="2xs">
                {turf.totalPassesScheduled}x scheduled
              </Badge>
            )}
          </HStack>
          {!isFollowUp && (
            <>
              <Progress.Root
                value={contactRate}
                size="xs"
                colorPalette={progressColor}
                mt={1}
                borderRadius="full">
                <Progress.Track>
                  <Progress.Range />
                </Progress.Track>
              </Progress.Root>
              <Text color="gray.500" fontSize="2xs" mt="2px">
                {turf.visitedCount}/{turf.memberCount} contacted ({contactRate}%)
              </Text>
            </>
          )}
        </Box>
      </HStack>
    </Box>
  );
}
